import React from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MapPin, CalendarDays, IndianRupee, ChevronRight } from "lucide-react";
import { Icon } from "radix-ui";

function getAttrValue(attributes, keyId) {
  if (!attributes) return undefined;
  const found = attributes.find((a) => a.AttrKeyID === keyId);
  return found?.AttrValue;
}

function TourPackageCard({ pkg, onOpen }) {
  // Normalize data: check if pkg has "Package" property, otherwise assume pkg is the package itself
  const pack = pkg.Package || pkg;
  const Destination = pkg.Destination || {};
  const Attributes = pkg.Attributes || [];

  const destinationName = Destination?.DestinationName;

  // Try to get image from FileList first, then fall back to Destination image
  let imageUrl = Destination?.ImageUrl;
  if (pack.FileList && pack.FileList.length > 0) {
    imageUrl = pack.FileList[0].FileURL;
  }

  const highlights = pack.Highlights
    ? pack.Highlights.split("|").map((h) => h.trim()).filter(Boolean)
    : [];

  const durationText = getAttrValue(Attributes, 8); // "5 Days / 4 Nights"
  const priceFromRaw = getAttrValue(Attributes, 7); // "559"
  const priceFrom = priceFromRaw ? Number(priceFromRaw) : undefined;

  return (
    <Card className="group h-full overflow-hidden border border-border bg-card hover:bg-accent hover:border-primary/60 transition-colors duration-150 flex flex-col">
      {/* Image */}
      <div className="relative h-44 w-full overflow-hidden">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={pack.Title}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center bg-muted text-xs text-muted-foreground">
            Image coming soon
          </div>
        )}

        {destinationName && (
          <div className="absolute top-2 left-2">
            <Badge className="bg-background/90 text-foreground border border-border text-[11px] px-2 py-0.5 rounded-full flex items-center gap-1 shadow-sm">
              <MapPin className="h-3 w-3 text-primary" />
              <span className="truncate max-w-[120px] sm:max-w-[160px]">
                {destinationName}
              </span>
            </Badge>
          </div>
        )}

        {typeof priceFrom === "number" && (
          <div className="absolute bottom-2 right-2">
            <div className="bg-background/90 border border-border px-2.5 py-1 rounded-full shadow-sm flex items-baseline gap-1 text-[11px]">
              <Icon className="h-3 w-3 text-primary">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#000000"
                  stroke-width="1"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M15 5h-11h3a4 4 0 1 1 0 8h-3l6 6" />
                  <path d="M21 17l-4.586 -4.414a2 2 0 0 0 -2.828 2.828l.707 .707" />
                </svg>

              </Icon>
              <span className="font-semibold text-sm">
                {priceFrom.toLocaleString()}
              </span>
              <span className="text-[10px] text-muted-foreground">NPR</span>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <CardHeader className="p-3 sm:p-4 space-y-1 flex-1">
        <CardTitle className="text-sm sm:text-base font-semibold line-clamp-2">
          {pack.Title}
        </CardTitle>

        <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
          {durationText && (
            <span className="inline-flex items-center gap-1">
              <CalendarDays className="h-3 w-3" />
              {durationText}
            </span>
          )}
        </div>

        {pack.Summary && (
          <CardDescription className="text-xs text-muted-foreground line-clamp-2 mt-1">
            {pack.Summary}
          </CardDescription>
        )}

        {/* {highlights.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-2">
            {highlights.slice(0, 3).map((h, idx) => (
              <Badge
                key={idx}
                variant="outline"
                className="rounded-full text-[10px] font-normal"
              >
                {h}
              </Badge>
            ))}
            {highlights.length > 3 && (
              <span className="text-[10px] text-muted-foreground">
                +{highlights.length - 3} more
              </span>
            )}
          </div>
        )} */}
      </CardHeader>

      <CardFooter className="p-3 sm:p-4 pt-0 flex items-center justify-between">
        <span className="text-[11px] text-muted-foreground">
          Curated trip by{" "}
          <span className="font-medium text-primary">yatratickets.com</span>
        </span>
        <Button
          size="sm"
          className=" h-8 px-3 text-xs font-medium"
          onClick={onOpen}
        >
          View details
          <ChevronRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      </CardFooter>
    </Card >
  );
}

export default TourPackageCard;
